import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Thumbnail from '../Thumbnail';
import ThumbnailNoImage from '../ThumbnailNoImage';
import ThumbnailTracked from '../ThumbnailTracked';
import * as Types from '../../types';

const ThumbnailList = ({
  thumbnails,
  onThumbnailClick,
  onThumbnailDoubleClick,
  onClickUntrack,
  activeDisplaySetInstanceUIDs = [],
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const hasAutoSwitchedRef = useRef(false); // Referencia para controlar la ejecución única

  useEffect(() => {
    const handleResize = () => {
      const isMobileDevice = /(Mobi|Android|iPhone|iPad|iPod)/.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Si el único activo es uno sin imagen, activar uno con imagen (solo una vez)
  useEffect(() => {
    if (!thumbnails || thumbnails.length === 0 || hasAutoSwitchedRef.current) {
      return;
    }

    const activeNoImage = thumbnails.find(
      thumb =>
        activeDisplaySetInstanceUIDs.includes(thumb.displaySetInstanceUID) &&
        thumb.componentType === 'thumbnailNoImage'
    );

    const hasOtherActive = thumbnails.some(
      thumb =>
        activeDisplaySetInstanceUIDs.includes(thumb.displaySetInstanceUID) &&
        thumb.componentType !== 'thumbnailNoImage'
    );

    if (activeNoImage && !hasOtherActive) {
      const firstWithImage = thumbnails.find(
        thumb =>
          (thumb.componentType === 'thumbnail' || thumb.componentType === 'thumbnailTracked') &&
          thumb.displaySetInstanceUID !== activeNoImage.displaySetInstanceUID
      );

      if (firstWithImage) {
        hasAutoSwitchedRef.current = true; // Marcamos como ejecutado
        onThumbnailClick(firstWithImage.displaySetInstanceUID);
      }
    }
  }, [thumbnails, activeDisplaySetInstanceUIDs, onThumbnailClick]);

  // Reordenar thumbnails: documentos (thumbnailNoImage) al final
  const sortedThumbnails = [
    ...thumbnails.filter(thumb => thumb.componentType !== 'thumbnailNoImage'),
    ...thumbnails.filter(thumb => thumb.componentType === 'thumbnailNoImage'),
  ];

  return (
    <div
      id="ohif-thumbnail-list"
      className={
        isMobile
          ? 'ohif-scrollbar study-min-height invisible-scrollbar flex flex-row py-5'
          : 'study-min-height ohif-scrollbar2 scrollbar-thin scrollbar-thumb-[#8e9da1] scrollbar-track-transparent flex overflow-y-hidden overflow-x-scroll py-5'
      }
    >
      <div className="scroll-content flex flex-row">
        {sortedThumbnails.map(
          ({
            displaySetInstanceUID,
            description,
            dragData,
            seriesNumber,
            numInstances,
            loadingProgress,
            modality,
            componentType,
            seriesDate,
            countIcon,
            isTracked,
            canReject,
            onReject,
            imageSrc,
            messages,
            imageAltText,
            isHydratedForDerivedDisplaySet,
          }) => {
            const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
            switch (componentType) {
              case 'thumbnail':
                return (
                  <Thumbnail
                    key={displaySetInstanceUID}
                    displaySetInstanceUID={displaySetInstanceUID}
                    dragData={dragData}
                    description={description}
                    seriesNumber={seriesNumber}
                    numInstances={numInstances}
                    countIcon={countIcon}
                    imageSrc={imageSrc}
                    imageAltText={imageAltText}
                    messages={messages}
                    isActive={isActive}
                    onClick={() => onThumbnailClick(displaySetInstanceUID)}
                    onDoubleClick={() => onThumbnailDoubleClick(displaySetInstanceUID)}
                  />
                );
              case 'thumbnailTracked':
                return (
                  <ThumbnailTracked
                    key={displaySetInstanceUID}
                    displaySetInstanceUID={displaySetInstanceUID}
                    dragData={dragData}
                    description={description}
                    seriesNumber={seriesNumber}
                    numInstances={numInstances}
                    loadingProgress={loadingProgress}
                    countIcon={countIcon}
                    imageSrc={imageSrc}
                    imageAltText={imageAltText}
                    messages={messages}
                    isTracked={isTracked}
                    isActive={isActive}
                    onClick={() => onThumbnailClick(displaySetInstanceUID)}
                    onDoubleClick={() => onThumbnailDoubleClick(displaySetInstanceUID)}
                    onClickUntrack={() => onClickUntrack(displaySetInstanceUID)}
                  />
                );
              case 'thumbnailNoImage':
                return (
                  <ThumbnailNoImage
                    isActive={isActive}
                    key={displaySetInstanceUID}
                    displaySetInstanceUID={displaySetInstanceUID}
                    dragData={dragData}
                    modality={modality}
                    modalityTooltip={_getModalityTooltip(modality)}
                    messages={messages}
                    seriesDate={seriesDate}
                    description={description}
                    canReject={canReject}
                    onReject={onReject}
                    onClick={() => onThumbnailClick(displaySetInstanceUID)}
                    onDoubleClick={() => onThumbnailDoubleClick(displaySetInstanceUID)}
                    isHydratedForDerivedDisplaySet={isHydratedForDerivedDisplaySet}
                  />
                );
              default:
                return null;
            }
          }
        )}
      </div>
    </div>
  );
};

ThumbnailList.propTypes = {
  thumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      displaySetInstanceUID: PropTypes.string.isRequired,
      imageSrc: PropTypes.string,
      imageAltText: PropTypes.string,
      seriesDate: PropTypes.string,
      seriesNumber: Types.StringNumber,
      numInstances: PropTypes.number,
      description: PropTypes.string,
      componentType: Types.ThumbnailType.isRequired,
      isTracked: PropTypes.bool,
      dragData: PropTypes.shape({
        type: PropTypes.string.isRequired,
      }),
      modality: PropTypes.string,
    })
  ),
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  onThumbnailClick: PropTypes.func.isRequired,
  onThumbnailDoubleClick: PropTypes.func.isRequired,
  onClickUntrack: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

function _getModalityTooltip(modality) {
  if (Object.prototype.hasOwnProperty.call(_modalityTooltips, modality)) {
    return _modalityTooltips[modality];
  }

  return 'Unknown';
}

const _modalityTooltips = {
  OT: 'Other',
};

export default ThumbnailList;
